"use client"

import { useEffect } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { reportClientError } from "@lib/util/client-error-report"

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  // NEXT_REDIRECT is Next.js's internal redirect control-flow, not a real error.
  // redirect() from a streaming server component can reach the error boundary
  // before the router processes it. We suppress the error UI while the router
  // navigates — the redirect fires immediately after.
  const isRedirect =
    error.message === "NEXT_REDIRECT" ||
    !!error.digest?.startsWith("NEXT_REDIRECT")

  useEffect(() => {
    if (isRedirect) return
    console.error(error)
    reportClientError({
      source: "error-boundary",
      error,
      extra: {
        digest: error.digest || null,
      },
    })  
  }, [error, isRedirect])

  if (isRedirect) return null

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 py-24 text-center">
      <p className="mb-4 text-[11px] font-medium uppercase tracking-[0.28em] text-[#3D5240]">
        Something went wrong
      </p>
      <h1 className="alif-serif mb-6 text-3xl font-light text-[#181512] small:text-4xl">
        An unexpected error occurred
      </h1>
      <p className="mb-10 max-w-md text-sm font-light leading-7 text-[#5f574f]">
        We apologise for the inconvenience. Please try again, or return to the
        store and continue shopping.
      </p>
      <div className="flex flex-col items-center gap-4 small:flex-row">
        <button
          onClick={reset}
          className="inline-flex h-11 items-center justify-center bg-[#181512] px-8 text-[11px] font-medium uppercase tracking-[0.22em] text-[#FFFFFF] transition hover:bg-[#2d2620]"
        >
          Try again
        </button>
        <LocalizedClientLink
          href="/"
          className="inline-flex h-11 items-center justify-center border border-[#181512] px-8 text-[11px] font-medium uppercase tracking-[0.22em] text-[#181512] transition hover:bg-[#181512] hover:text-[#FFFFFF]"
        >
          Back to store
        </LocalizedClientLink>
      </div>
    </div>
  )
}
